/**
 * CodeMirror language support for FlowScript.
 *
 * This is a second, independent tokenizer — a `StreamLanguage` one — and that
 * duplication is deliberate. The project tokenizer in `src/language` scans the
 * whole document to build an AST; CodeMirror needs to re-highlight a single
 * line after a keystroke without knowing what came before it beyond a small
 * state object. The two cannot be the same code.
 *
 * What is *not* duplicated is the keyword list: it is imported from the real
 * tokenizer, so highlighting can never drift from parsing.
 */
import { StreamLanguage, LanguageSupport, HighlightStyle } from '@codemirror/language';
import type { StringStream } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { STATEMENT_KEYWORDS, KEYWORDS } from '../language/tokenizer/tokens';
import { ANNOTATION_NAMES, ICON_SLUGS } from '../language/parser/metadata';

interface FlowScriptState {
  inBlockComment: boolean;
  /** True while we are still at the first token of a statement. */
  atStatementStart: boolean;
  /** True right after `function` or `call`, where the next word is a name. */
  expectingName: boolean;
}

/** Keywords that stay meaningful in the middle of a statement. */
const MID_STATEMENT_KEYWORDS = new Set([
  'else',
  'until',
  'each',
  'in',
  'yes',
  'no',
  'true',
  'false',
  'case',
  'default',
]);

const DELIMITERS = new Set(['{', '}', '(', ')', ';', ':', ',', '@', '"', '\\']);

export const flowScriptStreamParser = {
  name: 'flowscript',

  startState(): FlowScriptState {
    return { inBlockComment: false, atStatementStart: true, expectingName: false };
  },

  copyState(state: FlowScriptState): FlowScriptState {
    return { ...state };
  },

  token(stream: StringStream, state: FlowScriptState): string | null {
    if (state.inBlockComment) {
      while (!stream.eol()) {
        if (stream.match('*/')) {
          state.inBlockComment = false;
          return 'comment';
        }
        stream.next();
      }
      return 'comment';
    }

    if (stream.sol()) {
      // A fresh line always begins a statement unless the previous one is still
      // open, which for this language only happens inside a block comment.
      state.atStatementStart = true;
    }

    if (stream.eatSpace()) return null;

    // --- comments -------------------------------------------------------
    if (stream.match('//')) {
      stream.skipToEnd();
      return 'comment';
    }
    if (stream.match('/*')) {
      state.inBlockComment = true;
      while (!stream.eol()) {
        if (stream.match('*/')) {
          state.inBlockComment = false;
          return 'comment';
        }
        stream.next();
      }
      return 'comment';
    }

    // --- strings ---------------------------------------------------------
    if (stream.peek() === '"') {
      stream.next();
      let escaped = false;
      while (!stream.eol()) {
        const char = stream.next();
        if (escaped) {
          escaped = false;
          continue;
        }
        if (char === '\\') escaped = true;
        else if (char === '"') break;
      }
      state.atStatementStart = false;
      return 'string';
    }

    // --- annotations ------------------------------------------------------
    if (stream.peek() === '@') {
      stream.next();
      stream.eatWhile(/[\w-]/);
      state.atStatementStart = false;
      return 'meta';
    }

    // --- structure --------------------------------------------------------
    const char = stream.peek() ?? '';
    if (char === '{' || char === '}' || char === '(' || char === ')') {
      stream.next();
      // Braces and the closing paren of a header both start a new statement.
      state.atStatementStart = char === '{' || char === '}';
      state.expectingName = false;
      return 'bracket';
    }
    if (char === ';' || char === ':') {
      stream.next();
      state.atStatementStart = true;
      state.expectingName = false;
      return 'punctuation';
    }
    if (char === ',') {
      stream.next();
      return 'punctuation';
    }
    if (char === '\\') {
      stream.next();
      // The escape hatch: whatever follows is prose, not a keyword.
      state.atStatementStart = false;
      return 'escape';
    }

    // --- words -------------------------------------------------------------
    let word = '';
    while (!stream.eol()) {
      const next = stream.peek() ?? '';
      if (/\s/.test(next) || DELIMITERS.has(next)) break;
      if (
        next === '/' &&
        (stream.string[stream.pos + 1] === '/' || stream.string[stream.pos + 1] === '*')
      ) {
        break;
      }
      word += stream.next() ?? '';
    }
    if (word === '') {
      stream.next();
      return null;
    }

    const normalized = word.toLowerCase();
    const wasStatementStart = state.atStatementStart;
    const wasExpectingName = state.expectingName;
    state.atStatementStart = false;
    state.expectingName = false;

    if (wasExpectingName) return 'variableName';

    if (wasStatementStart && STATEMENT_KEYWORDS.has(normalized)) {
      if (normalized === 'function' || normalized === 'call') state.expectingName = true;
      // `for each` — keep the keyword mood alive for the second word.
      if (normalized === 'for') state.atStatementStart = true;
      return 'keyword';
    }

    if (MID_STATEMENT_KEYWORDS.has(normalized) && KEYWORDS.has(normalized)) {
      // `else`/`case`/`default` re-open a statement position.
      if (normalized === 'else' || normalized === 'case' || normalized === 'default') {
        state.atStatementStart = true;
      }
      return 'keyword';
    }

    return null;
  },

  languageData: {
    commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
    closeBrackets: { brackets: ['(', '{', '"'] },
    indentOnInput: /^\s*\}$/,
    wordChars: "-'!?.",
  },
};

export const flowScriptLanguage = StreamLanguage.define<FlowScriptState>(flowScriptStreamParser);

export function flowScriptLanguageSupport(): LanguageSupport {
  return new LanguageSupport(flowScriptLanguage);
}

/**
 * Highlight colours, drawn from the same custom properties as the rest of the
 * UI so the editor re-themes with everything else.
 */
export const flowScriptHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: 'var(--fs-accent)', fontWeight: '600' },
  { tag: tags.string, color: 'var(--fs-node-decision-stroke)' },
  { tag: tags.comment, color: 'var(--fs-ink-faint)', fontStyle: 'italic' },
  { tag: tags.meta, color: 'var(--fs-node-loop-stroke)' },
  { tag: tags.variableName, color: 'var(--fs-node-call-stroke)', fontWeight: '500' },
  { tag: tags.bracket, color: 'var(--fs-ink-muted)' },
  { tag: tags.punctuation, color: 'var(--fs-ink-faint)' },
  { tag: tags.escape, color: 'var(--fs-danger)' },
]);

/** Re-exported so completion sources stay in step with the highlighter. */
export const ANNOTATION_COMPLETIONS = ANNOTATION_NAMES;
export const ICON_COMPLETIONS = ICON_SLUGS;
